import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { FeedbackContext } from '~/components/feedback'
import { getAccountId } from '~/helpers'
import * as publisherService from '~/modules/retailMedia/services/publisher'

import type { DestinationCampaigns, DestinationSettings } from './types'

export { getPublisherDestinationSettingsKey as destinationSettingsKey } from '~/modules/retailMedia/services/publisher'
export const destinationCampaignsKey = 'destinationCampaigns'
const DEFAULT_CAMPAIGNS_PER_PAGE = 10

type ToggleAvailabilityArgs = {
  next: boolean
  handleLoading?: (value: boolean) => void
}

export const useDestinationRetail = () => {
  const { t } = useTranslation()
  const { toast } = useContext(FeedbackContext)
  const queryClient = useQueryClient()
  const accountId = getAccountId()
  const [campaignPage, setCampaignPage] = useState(1)
  const [campaignsPerPage, setCampaignsPerPage] = useState(
    DEFAULT_CAMPAIGNS_PER_PAGE
  )
  const destinationSettingsQueryKey = [
    publisherService.getPublisherDestinationSettingsKey,
    accountId
  ]

  const settingsQuery = useQuery({
    queryKey: destinationSettingsQueryKey,
    queryFn: async () => {
      const { data } =
        await publisherService.getPublisherDestinationSettingsService()
      return data
    }
  })

  const campaignsQuery = useQuery<DestinationCampaigns>({
    queryKey: [
      destinationCampaignsKey,
      accountId,
      campaignPage,
      campaignsPerPage
    ],
    queryFn: async () => {
      const { data } =
        await publisherService.getPublisherDestinationCampaignsService({
          page: campaignPage,
          quantity: campaignsPerPage
        })
      return data
    }
  })

  const { mutate: mutateAvailability } = useMutation({
    mutationFn: ({ next }: ToggleAvailabilityArgs) =>
      publisherService.updatePublisherDestinationSettingsService({
        available_for_new_campaigns: next
      }),
    onSuccess: async (_data, { next, handleLoading }) => {
      await queryClient.invalidateQueries({
        queryKey: destinationSettingsQueryKey
      })
      handleLoading?.(next)
    },
    onError: (_error, { next, handleLoading }) => {
      handleLoading?.(!next)
      toast.critical(t('common:messages.errorOccurred.message'))
    }
  })

  const settings: DestinationSettings | undefined = settingsQuery.data

  return {
    isPending: settingsQuery.isPending,
    settingsError: settingsQuery.isError,
    campaignsPending: campaignsQuery.isPending,
    campaignsError: campaignsQuery.isError,
    settings,
    campaigns: campaignsQuery.data ?? {
      total: 0,
      pages: 0,
      currentPage: campaignPage,
      data: []
    },
    campaignPage,
    campaignsPerPage,
    changeCampaignPage: setCampaignPage,
    changeCampaignsPerPage: (quantity: number) => {
      setCampaignsPerPage(quantity)
      setCampaignPage(1)
    },
    availableForNewCampaigns: settings?.available_for_new_campaigns ?? false,
    toggleAvailability: (
      next: boolean,
      handleLoading?: (value: boolean) => void
    ) => mutateAvailability({ next, handleLoading })
  }
}
